import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { Check } from "lucide-react";
import { toast } from "sonner";

export default function Clube() {
    const [plans, setPlans] = useState([]);
    const { user } = useAuth();
    const navigate = useNavigate();
    const [busy, setBusy] = useState(null);

    useEffect(() => {
        api.get("/subscription-plans").then((r) => setPlans(r.data));
    }, []);

    async function subscribe(planId) {
        if (!user) {
            navigate("/login?next=/clube");
            return;
        }
        setBusy(planId);
        try {
            const { data } = await api.post("/subscriptions/checkout", {
                plan_id: planId,
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
            setBusy(null);
        }
    }

    return (
        <>
            <Navbar variant="dark" />
            <section className="relative pt-40 pb-24 bg-wine-dark text-cream overflow-hidden">
                <div className="absolute inset-0 azulejo-bg" />
                <div className="relative container-edge text-center">
                    <span className="label-overline text-gold-soft">Clube Caseirinho</span>
                    <h1 className="mt-6 font-heading text-5xl md:text-7xl leading-[0.95] max-w-4xl mx-auto">
                        Uma curadoria mensal de vinhos portugueses,{" "}
                        <em className="text-gold-soft not-italic">à sua porta</em>.
                    </h1>
                    <p className="mt-8 max-w-2xl mx-auto text-cream/75 text-lg">
                        Três planos, uma promessa: pequenos produtores, edições especiais,
                        harmonização e cartas de prova incluídas em cada entrega.
                    </p>
                </div>
            </section>
            <section className="bg-cream py-20 md:py-28">
                <div className="container-edge grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                    {plans.map((plan, idx) => (
                        <div
                            key={plan.id}
                            className={`bg-white p-10 rounded-md flex flex-col gap-6 border ${
                                idx === 1 ? "border-gold-soft shadow-xl md:-translate-y-4" : "border-gold-soft/20 shadow-sm"
                            }`}
                            data-testid={`plan-${plan.id}`}
                        >
                            {idx === 1 && <span className="label-overline text-gold-soft">Mais escolhido</span>}
                            <h3 className="font-heading text-3xl text-wine-dark">{plan.name}</h3>
                            <p className="text-sm text-wine-dark/70 leading-relaxed min-h-[60px]">{plan.description}</p>
                            <div className="flex items-baseline gap-1">
                                <span className="font-heading text-5xl text-wine-dark">€{plan.price.toFixed(2)}</span>
                                <span className="text-sm text-wine-dark/60">/mês</span>
                            </div>
                            <ul className="space-y-3 text-sm text-wine-dark/80">
                                <li className="flex items-center gap-3">
                                    <Check className="h-4 w-4 text-vineyard" strokeWidth={2} /> {plan.bottles} garrafas curadas
                                </li>
                                <li className="flex items-center gap-3">
                                    <Check className="h-4 w-4 text-vineyard" strokeWidth={2} /> Cartas de prova editoriais
                                </li>
                                <li className="flex items-center gap-3">
                                    <Check className="h-4 w-4 text-vineyard" strokeWidth={2} /> Acesso a eventos exclusivos
                                </li>
                                <li className="flex items-center gap-3">
                                    <Check className="h-4 w-4 text-vineyard" strokeWidth={2} /> Pause ou cancele a qualquer momento
                                </li>
                            </ul>
                            <button
                                onClick={() => subscribe(plan.id)}
                                disabled={busy === plan.id}
                                className={idx === 1 ? "btn-gold mt-auto" : "btn-wine mt-auto"}
                                data-testid={`subscribe-${plan.id}`}
                            >
                                {busy === plan.id ? "A processar…" : "Assinar"}
                            </button>
                        </div>
                    ))}
                </div>
            </section>
            <Footer />
        </>
    );
}
